/**
 * Persists the full multi-step registration form locally (post-success),
 * since the API only stores a subset. Used by Pengaturan to show what the user submitted.
 */
import { defaultIdentitasProduk, defaultIdentitasUsaha } from '@/lib/identitasDefaults';
import { BUSINESS_SCALE_OPTIONS } from '@/lib/clientBusinessScaleEnums';
import { MARKETING_AREA_OPTIONS, MARKETING_SYSTEM_OPTIONS } from '@/lib/clientMarketingEnums';

export const REGISTRATION_SNAPSHOT_STORAGE_KEY = 'halalin_registration_snapshot';

function businessScaleLabel(code) {
  const o = BUSINESS_SCALE_OPTIONS.find((x) => x.value === code);
  return o ? o.label : displayValue(code);
}

function marketingAreaLabel(code) {
  const o = MARKETING_AREA_OPTIONS.find((x) => x.value === code);
  return o ? o.label : displayValue(code);
}

function marketingSystemLabel(code) {
  const o = MARKETING_SYSTEM_OPTIONS.find((x) => x.value === code);
  return o ? o.label : displayValue(code);
}

function displayValue(v) {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'Ya' : 'Tidak';
  if (Array.isArray(v)) {
    if (v.length === 0) return '—';
    return v
      .map((item) => (typeof item === 'object' && item !== null ? item.name ?? String(item) : String(item)))
      .join(', ');
  }
  const s = String(v).trim();
  return s || '—';
}

const PERALATAN = { modern: 'Modern', sederhana: 'Sederhana' };
const KEPEMILIKAN = {
  'milik-sendiri': 'Milik sendiri',
  franchise: 'Franchise',
  kemitraan: 'Kemitraan',
};

function sanitizeIdentitasUsaha(u) {
  if (!u || typeof u !== 'object') return {};
  const {
    alamatProduksiSama: _rm1,
    alamatFasilitasSama: _rm2,
    skalaBisnis: _dropSkala,
    hasNIB: _dropHasNib,
    tenagaKerja: legacyTk,
    jumlahKaryawan: jk,
    ...rest
  } = u;
  const digits =
    String(jk ?? '')
      .replace(/\D/g, '') ||
    (legacyTk != null && legacyTk !== '' ? String(legacyTk).replace(/\D/g, '') : '');
  const { sistemPemasaran: _dropPemasaran, ...clean } = rest;
  return { ...clean, jumlahKaryawan: digits };
}

/**
 * @param {{
 *   userId?: string | null;
 *   accountEmail?: string | null;
 *   source?: 'registration' | 'settings' | null;
 *   pelakuBisnis?: object;
 *   identitasUsaha?: object;
 *   identitasProduk?: object;
 * }} data
 */
export function saveRegistrationSnapshot(data) {
  const pelakuClean = { ...(data.pelakuBisnis || {}) };
  delete pelakuClean.password;
  delete pelakuClean.confirmPassword;

  const produkRaw = data.identitasProduk || {};
  const uploadedFiles = Array.isArray(produkRaw.uploadedFiles)
    ? produkRaw.uploadedFiles.map((f) =>
        typeof f === 'object' && f !== null ? { name: f.name ?? '', status: f.status } : { name: String(f) },
      )
    : [];

  const source =
    data.source === 'registration' || data.source === 'settings' ? data.source : null;

  const payload = {
    savedAt: new Date().toISOString(),
    userId: data.userId ?? null,
    accountEmail: data.accountEmail ? String(data.accountEmail).trim().toLowerCase() : null,
    source,
    pelakuBisnis: pelakuClean,
    identitasUsaha: sanitizeIdentitasUsaha(data.identitasUsaha || {}),
    identitasProduk: {
      ...produkRaw,
      uploadedFiles,
    },
  };

  localStorage.setItem(REGISTRATION_SNAPSHOT_STORAGE_KEY, JSON.stringify(payload));
}

export function loadRegistrationSnapshot() {
  try {
    const raw = localStorage.getItem(REGISTRATION_SNAPSHOT_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Snapshot hanya dipakai jika milik pengguna yang sedang login (satu browser bisa ganti akun).
 * @param {{ id?: string | null; email?: string | null }} user
 */
export function getRegistrationSnapshotForUser(user) {
  const snap = loadRegistrationSnapshot();
  if (!snap) return null;
  const uid = user?.id ? String(user.id) : '';
  const email = user?.email ? String(user.email).trim().toLowerCase() : '';
  if (snap.userId && uid && String(snap.userId) !== uid) return null;
  const snapEmail =
    snap.accountEmail ||
    (snap.pelakuBisnis?.email ? String(snap.pelakuBisnis.email).trim().toLowerCase() : '');
  if (email && snapEmail && snapEmail !== email) return null;
  return snap;
}

/** Identitas usaha snapshot is complete enough to leave wizard step 1 (matches Onboarding validation). */
export function isIdentitasUsahaSnapshotCompleteForGate(user) {
  if (!user?.client_id) return false;
  const snap = getRegistrationSnapshotForUser(user);
  const u = snap?.identitasUsaha;
  if (!u) return false;
  if (!String(u.namaPerusahaan ?? '').trim()) return false;
  if (!String(u.alamatPerusahaan ?? '').trim()) return false;
  if (!String(u.provinsiUsaha ?? '').trim()) return false;
  if (!String(u.kotaUsaha ?? '').trim()) return false;
  const jk = String(u.jumlahKaryawan ?? '').replace(/\D/g, '');
  if (!jk || parseInt(jk, 10) < 1) return false;
  return true;
}

/**
 * Human-facing blockers before pemohon may submit pre-assessment (review pengajuan).
 * Requires client link, identitas usaha snapshot (same rules as dashboard gate), and
 * ringkasan PIC / jenis produk seperti di Pengaturan → Data usaha & PIC.
 * @param {{ id?: string | null; email?: string | null; client_id?: string | null }} user
 * @param {Record<string, string>} [businessDataFallback]
 * @returns {string[]}
 */
export function getClientPengajuanBlockers(user, businessDataFallback = {}) {
  const blockers = [];
  if (!user?.client_id) {
    blockers.push('Akun belum terhubung ke client.');
    return blockers;
  }
  const snap = getRegistrationSnapshotForUser(user);
  const u = snap?.identitasUsaha;
  if (!u) {
    blockers.push('Identitas usaha belum lengkap (nama, alamat, provinsi, kota, jumlah karyawan).');
  } else {
    if (!String(u.namaPerusahaan ?? '').trim()) blockers.push('Nama usaha');
    if (!String(u.alamatPerusahaan ?? '').trim()) blockers.push('Alamat usaha');
    if (!String(u.provinsiUsaha ?? '').trim()) blockers.push('Provinsi usaha');
    if (!String(u.kotaUsaha ?? '').trim()) blockers.push('Kota / kabupaten usaha');
    const jk = String(u.jumlahKaryawan ?? '').replace(/\D/g, '');
    if (!jk || parseInt(jk, 10) < 1) blockers.push('Jumlah karyawan (minimal 1)');
  }

  const biz = getBusinessSummaryForSettings(user, businessDataFallback);
  if (!String(biz.productType ?? '').trim()) blockers.push('Jenis produk');
  if (!String(biz.picName ?? '').trim()) blockers.push('Nama PIC');
  if (!String(biz.picPhone ?? '').trim()) blockers.push('Telepon PIC');
  if (!String(biz.picEmail ?? '').trim()) blockers.push('Email PIC');

  return blockers;
}

/** @param {Record<string, string>} [businessDataFallback] */
export function isClientDataCompleteForPengajuan(user, businessDataFallback) {
  return getClientPengajuanBlockers(user, businessDataFallback).length === 0;
}

/**
 * True when pemohon may access routes behind OnboardingGate (pembayaran terverifikasi).
 * Identitas usaha dilengkapi di dashboard setelah pembayaran.
 * PaymentGate mengarahkan ke /onboarding sampai `payment_status === VERIFIED`.
 */
export function isRegistrationOnboardingComplete(user) {
  return user?.payment_status === 'VERIFIED';
}

/**
 * Ringkasan usaha / PIC untuk form Pengaturan (snapshot milik user atau fallback `application.businessData`).
 * @param {{ id?: string | null; email?: string | null }} user
 * @param {Record<string, string>} businessDataFallback
 * @param {ReturnType<typeof getRegistrationSnapshotForUser>} [registrationSnapshot] — jika diisi, dipakai menggantikan `getRegistrationSnapshotForUser(user)` (mis. snapshot yang sudah di-memo di halaman Pengaturan).
 */
export function getBusinessSummaryForSettings(user, businessDataFallback = {}, registrationSnapshot) {
  const snap =
    registrationSnapshot !== undefined ? registrationSnapshot : getRegistrationSnapshotForUser(user);
  const b = businessDataFallback || {};
  if (snap) {
    const p = snap.pelakuBisnis || {};
    const u = snap.identitasUsaha || {};
    const pr = snap.identitasProduk || {};
    // Snapshot can lag behind Pengaturan (/me tenant_profile → businessData). Prefer snapshot text,
    // but fall back so Pre-Assessment PATCH does not wipe server fields with blanks.
    return {
      companyName: String(u.namaPerusahaan ?? b.companyName ?? '').trim(),
      address: String(u.alamatPerusahaan ?? b.address ?? '').trim(),
      productType: String(pr.jenisProduk ?? b.productType ?? '').trim(),
      picName: String(p.namaLengkap ?? b.picName ?? '').trim(),
      picPhone: String(p.noTelp ?? b.picPhone ?? '').trim(),
      picEmail: String(p.email ?? b.picEmail ?? '').trim(),
      nib: String(u.nomorNibUsaha ?? b.nib ?? '').trim(),
      distributionPermit: String(u.nomorIzinEdar ?? b.distributionPermit ?? '').trim(),
      businessScale: String(u.businessScale ?? b.businessScale ?? '').trim(),
      marketingArea: String(b.marketingArea ?? '').trim(),
      marketingSystem: String(b.marketingSystem ?? '').trim(),
    };
  }
  return {
    companyName: String(b.companyName ?? '').trim(),
    address: String(b.address ?? '').trim(),
    productType: String(b.productType ?? '').trim(),
    picName: String(b.picName ?? '').trim(),
    picPhone: String(b.picPhone ?? '').trim(),
    picEmail: String(b.picEmail ?? '').trim(),
    nib: String(b.nib ?? '').trim(),
    distributionPermit: String(b.distributionPermit ?? '').trim(),
    businessScale: String(b.businessScale ?? '').trim(),
    marketingArea: String(b.marketingArea ?? '').trim(),
    marketingSystem: String(b.marketingSystem ?? '').trim(),
  };
}

/**
 * Gabungkan ringkasan ke snapshot lokal; tetap mempertahankan `source: 'registration'` jika dari halaman Daftar.
 * @param {{ id?: string | null; email?: string | null }} user
 * @param {ReturnType<typeof getBusinessSummaryForSettings>} summary
 */
export function applyBusinessSummaryToSnapshot(user, summary) {
  const existing = getRegistrationSnapshotForUser(user);
  const pelakuBase = { ...(existing?.pelakuBisnis || {}) };
  const usahaBase = { ...(existing?.identitasUsaha || {}) };
  const produkBase = { ...(existing?.identitasProduk || {}) };
  delete pelakuBase.password;
  delete pelakuBase.confirmPassword;

  const pelaku = {
    ...pelakuBase,
    namaLengkap: summary.picName ?? '',
    noTelp: summary.picPhone ?? '',
    email: summary.picEmail ?? '',
  };
  const usaha = {
    ...usahaBase,
    namaPerusahaan: summary.companyName ?? '',
    alamatPerusahaan: summary.address ?? '',
    nomorNibUsaha: summary.nib ?? '',
    nomorIzinEdar: summary.distributionPermit ?? '',
    businessScale: summary.businessScale ?? '',
  };
  const produk = {
    ...produkBase,
    jenisProduk: summary.productType ?? '',
  };

  const source =
    existing == null ? 'settings' : existing.source === 'settings' ? 'settings' : 'registration';

  saveRegistrationSnapshot({
    userId: user?.id ?? existing?.userId ?? null,
    accountEmail: user?.email || existing?.accountEmail || pelaku.email || null,
    source,
    pelakuBisnis: pelaku,
    identitasUsaha: usaha,
    identitasProduk: produk,
  });
}

/**
 * Human-readable sections for Settings UI (labels match Registration.jsx).
 * @param {ReturnType<loadRegistrationSnapshot>} snap
 * @returns {{ title: string, description: string, rows: { id: string, label: string, value: string }[] }[]}
 */
export function getRegistrationReviewSections(snap) {
  if (!snap) return [];

  const p = snap.pelakuBisnis || {};
  const u = snap.identitasUsaha || {};
  const pr = snap.identitasProduk || {};

  const sections = [
    {
      title: 'Identitas pelaku usaha',
      description: 'Langkah 1 registrasi — data diri pelaku usaha.',
      rows: [
        ['settings-reg-pelaku-nama', 'Nama lengkap', displayValue(p.namaLengkap)],
        ['settings-reg-pelaku-alamat', 'Alamat (sesuai KTP)', displayValue(p.alamat)],
        ['settings-reg-pelaku-provinsi', 'Provinsi', displayValue(p.provinsi)],
        ['settings-reg-pelaku-kota', 'Kota / Kabupaten', displayValue(p.kota)],
        ['settings-reg-pelaku-kelurahan', 'Kelurahan', displayValue(p.kelurahan)],
        ['settings-reg-pelaku-kecamatan', 'Kecamatan', displayValue(p.kecamatan)],
        ['settings-reg-pelaku-rt', 'RT', displayValue(p.rt)],
        ['settings-reg-pelaku-rw', 'RW', displayValue(p.rw)],
        ['settings-reg-pelaku-kodepos', 'Kode pos', displayValue(p.kodePos)],
        ['settings-reg-pelaku-telp', 'No. telepon pelaku usaha', displayValue(p.noTelp)],
        ['settings-reg-pelaku-email', 'Email', displayValue(p.email)],
      ].map(([id, label, value]) => ({ id, label, value })),
    },
    {
      title: 'Identitas usaha',
      description: 'Langkah 2 registrasi — perusahaan dan lokasi usaha.',
      rows: [
        ['settings-reg-usaha-nama', 'Nama perusahaan', displayValue(u.namaPerusahaan)],
        ['settings-reg-usaha-nib', 'NIB', displayValue(u.nomorNibUsaha)],
        ['settings-reg-usaha-skala', 'Skala usaha', businessScaleLabel(u.businessScale)],
        ['settings-reg-usaha-izin-edar', 'Nomor izin edar', displayValue(u.nomorIzinEdar)],
        [
          'settings-reg-usaha-jk',
          'Jumlah karyawan',
          displayValue(u.jumlahKaryawan ?? u.tenagaKerja),
        ],
        ['settings-reg-usaha-alamat', 'Alamat perusahaan', displayValue(u.alamatPerusahaan)],
        ['settings-reg-usaha-prov', 'Provinsi', displayValue(u.provinsiUsaha)],
        ['settings-reg-usaha-kota', 'Kota', displayValue(u.kotaUsaha)],
        ['settings-reg-usaha-kel', 'Kelurahan', displayValue(u.kelurahanUsaha)],
        ['settings-reg-usaha-kec', 'Kecamatan', displayValue(u.kecamatanUsaha)],
        ['settings-reg-usaha-rt', 'RT', displayValue(u.rtUsaha)],
        ['settings-reg-usaha-rw', 'RW', displayValue(u.rwUsaha)],
        ['settings-reg-usaha-kodepos', 'Kode pos', displayValue(u.kodePosUsaha)],
        ['settings-reg-usaha-daerah-pemasaran', 'Daerah pemasaran', marketingAreaLabel(u.marketingArea)],
        ['settings-reg-usaha-sistem-pemasaran', 'Sistem pemasaran', marketingSystemLabel(u.marketingSystem)],
      ].map(([id, label, value]) => ({ id, label, value })),
    },
    {
      title: 'Identitas produk dan produksi',
      description: 'Langkah 3 registrasi — produk, peralatan, dan dokumen pendukung.',
      rows: [
        ['settings-reg-prod-nama', 'Nama produk', displayValue(pr.namaProduk)],
        [
          'settings-reg-prod-impor',
          'Impor',
          pr.isImpor === null || pr.isImpor === undefined ? '—' : pr.isImpor ? 'Ya' : 'Tidak',
        ],
        ['settings-reg-prod-ruang', 'Ruang lingkup', displayValue(pr.ruangLingkup)],
        ['settings-reg-prod-jenis', 'Jenis produk', displayValue(pr.jenisProduk)],
        ['settings-reg-prod-merk', 'Merk dagang', displayValue(pr.merkDagang)],
        ['settings-reg-prod-alat', 'Peralatan', displayValue(PERALATAN[pr.peralatan] || pr.peralatan)],
        ['settings-reg-prod-harga', 'Harga satuan', displayValue(pr.hargaSatuan)],
        ['settings-reg-prod-kapasitas', 'Kapasitas produk (per hari)', displayValue(pr.kapasitasProduk)],
        ['settings-reg-prod-menu', 'Berkas menu / unggahan', displayValue(pr.uploadedFiles)],
        ['settings-reg-prod-outlet', 'Jumlah outlet', displayValue(pr.jumlahOutlet)],
        [
          'settings-reg-prod-milik',
          'Jenis kepemilikan',
          displayValue(KEPEMILIKAN[pr.jenisKepemilikan] || pr.jenisKepemilikan),
        ],
        ['settings-reg-prod-gudang', 'Lokasi gudang', displayValue(pr.lokasiGudang)],
        ['settings-reg-prod-armada', 'Jumlah armada', displayValue(pr.jumlahArmada)],
        ['settings-reg-prod-maklon', 'Jumlah maklon', displayValue(pr.jumlahMaklon)],
        ['settings-reg-prod-syarat', 'Setuju syarat dan ketentuan', displayValue(pr.agreeTerms)],
      ].map(([id, label, value]) => ({ id, label, value })),
    },
  ];

  return sections;
}

/**
 * Ensure local registration snapshot exists so "Data perusahaan" can be edited (source `settings`).
 * @param {{ id?: string | null; email?: string | null; name?: string | null }} user
 */
export function ensureRegistrationSnapshot(user) {
  const existing = getRegistrationSnapshotForUser(user);
  if (existing) return existing;

  const pelakuBisnis = {
    namaLengkap: user?.name || '',
    alamat: '',
    provinsi: '',
    kota: '',
    kelurahan: '',
    kecamatan: '',
    rt: '',
    rw: '',
    kodePos: '',
    noTelp: '',
    email: user?.email || '',
  };

  saveRegistrationSnapshot({
    userId: user?.id ?? null,
    accountEmail: user?.email ? String(user.email).trim().toLowerCase() : null,
    source: 'settings',
    pelakuBisnis,
    identitasUsaha: { ...defaultIdentitasUsaha },
    identitasProduk: { ...defaultIdentitasProduk, uploadedFiles: [] },
  });
  return getRegistrationSnapshotForUser(user);
}

/**
 * Merge one company-data group into the snapshot and persist (localStorage).
 * @param {{ id?: string | null; email?: string | null }} user
 * @param {'pelaku'|'usaha'|'produk'} namespace
 * @param {Record<string, unknown>} patch
 */
export function persistCompanyDataSection(user, namespace, patch) {
  const existing = ensureRegistrationSnapshot(user);
  const src = existing.source === 'registration' ? 'registration' : 'settings';

  const merged = {
    userId: existing.userId ?? user?.id ?? null,
    accountEmail:
      existing.accountEmail ?? (user?.email ? String(user.email).trim().toLowerCase() : null),
    source: src,
    pelakuBisnis: { ...(existing.pelakuBisnis || {}) },
    identitasUsaha: { ...defaultIdentitasUsaha, ...(existing.identitasUsaha || {}) },
    identitasProduk: {
      ...defaultIdentitasProduk,
      ...(existing.identitasProduk || {}),
      uploadedFiles: Array.isArray(existing.identitasProduk?.uploadedFiles)
        ? existing.identitasProduk.uploadedFiles
        : [],
    },
  };

  if (namespace === 'pelaku') {
    merged.pelakuBisnis = { ...merged.pelakuBisnis, ...patch };
  } else if (namespace === 'usaha') {
    merged.identitasUsaha = { ...merged.identitasUsaha, ...patch };
  } else if (namespace === 'produk') {
    const prevFiles = merged.identitasProduk.uploadedFiles;
    merged.identitasProduk = {
      ...merged.identitasProduk,
      ...patch,
    };
    if (patch.uploadedFiles === undefined) {
      merged.identitasProduk.uploadedFiles = prevFiles;
    }
  }

  saveRegistrationSnapshot(merged);
}

/** Display helper for settings read-only cells (e.g. uploaded file list). */
export function formatSnapshotFieldDisplay(v) {
  return displayValue(v);
}
